import { useEffect, useState } from 'react'

import { useDeviceDrawerData } from '../../hooks/useDeviceDrawerData'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'

import { Box, Flex } from '@hivewatch/dark'

import { EditDeviceFieldsI } from '@/features/device-crud'
import { DeviceFeed } from '@/features/device-feed'
import {
  DRAWER_VIDEO_PLAYER_HEIGHT,
  DRAWER_VIDEO_PLAYER_WIDTH,
  LazyLiveKitVideoPlayer,
  MSEVideoPlayer,
  VideoFeedEvent,
  VideoPlayer,
  useFindDeviceFeed,
} from '@/features/video-player'
import { DeviceType } from '@/graphql/generated/schemas'
import {
  AnalyticsScreen,
  BUTTON_PRESS,
  trackEvent,
  useMixpanel,
} from '@/utils/analytics'

import { DarkDeviceDrawer } from './DarkDeviceDrawer'

interface IProps {
  defaultIndex: number
  deviceId: string
  isOpen: boolean
  onClose: () => void
  onEditClick: (deviceData: EditDeviceFieldsI) => void
}

/**
 * Rebrand (ENG-32123) access-control (door) device drawer. Wraps the shared
 * `DarkDeviceDrawer` chrome and supplies the multi-camera video region. The
 * video feed logic is unchanged from the legacy `ACDeviceDrawer` — the dark
 * video-player component is a later phase.
 */
export const DarkACDeviceDrawer = ({
  defaultIndex,
  deviceId,
  isOpen,
  onClose,
  onEditClick,
}: IProps) => {
  const { t } = useTranslation()
  const { device, incidents, isIncidentsLoading, isLoading } =
    useDeviceDrawerData(deviceId)
  const [isPipActive, setPIPActive] = useState(false)

  // Feature flag for MSE-based authenticated proxy streaming
  const isMSEStreamingEnabled = useFeatureFlag(
    FeatureFlag.authenticatedProxyStreaming
  )
  // Feature flag for Lumana cloud WebRTC live view (ENG-31640). Mirrors the
  // camera drawer's gate so an associated Lumana camera plays the same way
  // whether opened directly or via its door (ENG-33344).
  const isLumanaStreamEnabled = useFeatureFlag(FeatureFlag.lumanaCameraStream)

  const {
    createCameraEventHandler,
    createPlayerActionHandler,
    markStreamRequested,
  } = useMixpanel(AnalyticsScreen.DeviceDrawer)

  useEffect(() => {
    if (isOpen) {
      markStreamRequested()
    }
  }, [isOpen])

  const mixpanelData = () => {
    return {
      device_id: deviceId,
      facility_id: device?.facility?.id,
      floor_id: device?.floor?.id,
      floor_name: device?.floor?.name,
      streaming_type: device?.camera?.streamingType,
    }
  }
  const handleCameraEvent = createCameraEventHandler(mixpanelData())
  const handlePlayerAction = createPlayerActionHandler(mixpanelData())

  const {
    stream: {
      url: cameraFeedUrl,
      isLoading: isCameraFeedLoading,
      stream: cameraStream,
    },
    nextCamera,
    previousCamera,
    availableCamerasIds,
    currentCameraId,
    errorMessage,
  } = useFindDeviceFeed(device?.id, handleCameraEvent)

  const liveKitStream = cameraStream?.kind === 'livekit' ? cameraStream : null

  const disablePIP = () => setPIPActive(false)

  const handleEditClick = () => {
    if (device) {
      trackEvent(`${BUTTON_PRESS} ACDeviceDrawer Edit Door`, mixpanelData())
      onEditClick({
        id: deviceId,
        floorId: device.floor?.id,
        name: device.name,
        type: DeviceType.Door,
      })
    }
  }

  const videoSlot = (
    <DeviceFeed
      isLoading={isCameraFeedLoading}
      onClickNext={nextCamera}
      onClickPrevious={previousCamera}
      showCameraSwitcher={availableCamerasIds.length > 1}
    >
      {isLumanaStreamEnabled && liveKitStream && currentCameraId ? (
        <LazyLiveKitVideoPlayer
          apiUrl={liveKitStream.apiUrl}
          cameraId={currentCameraId}
          errorLabel={errorMessage}
          onEvent={handleCameraEvent}
          onPlayerControlAction={handlePlayerAction}
          onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
          token={liveKitStream.token}
        />
      ) : isMSEStreamingEnabled && currentCameraId ? (
        <MSEVideoPlayer
          autoplay={true}
          cameraId={currentCameraId}
          errorLabel={errorMessage}
          onEvent={handleCameraEvent}
          onPlayerControlAction={handlePlayerAction}
          onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
          showControls={true}
        />
      ) : cameraFeedUrl ? (
        <VideoPlayer
          disablePIP={disablePIP}
          errorLabel={errorMessage}
          liveEnabled
          liveOnly
          onPlayerControlAction={handlePlayerAction}
          onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
          pipActive={isPipActive}
          url={cameraFeedUrl}
        />
      ) : (
        <Flex
          alignItems='center'
          bg='black'
          borderRadius='0.375rem'
          h={DRAWER_VIDEO_PLAYER_HEIGHT}
          justifyContent='center'
          w={DRAWER_VIDEO_PLAYER_WIDTH}
        >
          <Box color='white'>
            {errorMessage ?? t('devices:drawer_video_unavailable')}
          </Box>
        </Flex>
      )}
    </DeviceFeed>
  )

  return (
    <DarkDeviceDrawer
      defaultIndex={defaultIndex}
      device={device}
      deviceId={deviceId}
      deviceType={DeviceType.Door}
      incidents={incidents}
      isIncidentsLoading={isIncidentsLoading}
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      onEdit={handleEditClick}
      videoSlot={videoSlot}
    />
  )
}
